import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Swords, History, Trophy, BarChart3, Award, Bell, User, Settings, LogOut, Sun, Moon, Globe, HelpCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const NAV = [
  { to: "/dashboard", key: "nav.dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/play", key: "nav.play", icon: Swords, testId: "nav-play" },
  { to: "/history", key: "nav.history", icon: History, testId: "nav-history" },
  { to: "/leaderboards", key: "nav.leaderboards", icon: Trophy, testId: "nav-leaderboards" },
  { to: "/stats", key: "nav.stats", icon: BarChart3, testId: "nav-stats" },
  { to: "/rewards", key: "nav.rewards", icon: Award, testId: "nav-rewards" },
];

export function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let mounted = true;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/notifications/unread-count");
        if (mounted) setUnread(data.count || 0);
      } catch (_) {}
    };
    fetchUnread();
    const int = setInterval(fetchUnread, 30000);
    return () => { mounted = false; clearInterval(int); };
  }, []);

  const isDark = document.documentElement.classList.contains("dark");

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top nav */}
      <header className="glass-nav sticky top-0 z-40" data-testid="app-header">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/dashboard" data-testid="header-brand-link"><Logo /></Link>
            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map(({ to, key, icon: Icon, testId }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={testId}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" /> {t(key)}
                </NavLink>
              ))}
              {user?.role === "admin" && (
                <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive ? "bg-accent/10 text-accent" : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                  }`}>
                  <Settings className="w-4 h-4" /> {t("nav.admin")}
                </NavLink>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" data-testid="theme-toggle-btn"
              onClick={() => setTheme(isDark ? "light" : "dark")} aria-label="Toggle theme">
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="icon" data-testid="language-toggle-btn"
              onClick={() => setLang(lang === "fr" ? "en" : "fr")} aria-label="Toggle language">
              <span className="text-xs font-semibold uppercase tracking-wider">{lang}</span>
            </Button>
            <Button variant="ghost" size="icon" data-testid="header-help-btn"
              onClick={() => navigate("/help")} aria-label="Help">
              <HelpCircle className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" data-testid="notif-button" onClick={() => navigate("/notifications")}>
              <div className="relative">
                <Bell className="w-4 h-4" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </div>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="rounded-full">
                  <Avatar className="h-9 w-9 border border-border">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                      {user?.name?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="font-semibold">{user?.name}</span>
                  <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                  <Badge className="mt-2 w-fit" variant="secondary">Lvl {user?.level || 1} · {user?.xp || 0} XP</Badge>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" /> {t("nav.profile")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="menu-notif">
                  <Bell className="w-4 h-4 mr-2" /> {t("nav.notifications")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="lg:hidden border-t border-border overflow-x-auto">
          <div className="flex items-center gap-1 px-2 py-2 min-w-max">
            {NAV.map(({ to, key, icon: Icon, testId }) => (
              <NavLink key={to} to={to} data-testid={`m-${testId}`}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium ${
                    isActive ? "bg-primary/10 text-primary" : "text-muted-foreground"
                  }`}>
                <Icon className="w-3.5 h-3.5" /> {t(key)}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <NavLink to="/admin" data-testid="m-nav-admin"
                className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium ${
                  isActive ? "bg-accent/10 text-accent" : "text-muted-foreground"}`}>
                <Settings className="w-3.5 h-3.5" /> {t("nav.admin")}
              </NavLink>
            )}
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">{children}</main>

      <footer className="mx-auto max-w-7xl px-4 sm:px-6 py-8 border-t border-border mt-16 text-xs text-muted-foreground flex flex-col sm:flex-row justify-between gap-2">
        <span>© {new Date().getFullYear()} CBC Global Holdings Ltd — Globo</span>
        <span className="font-editorial italic">Le Jeu de l'Émergence</span>
      </footer>
    </div>
  );
}
